import { zodResolver } from '@hookform/resolvers/zod';
import type { Resolver } from 'react-hook-form';
import { z } from 'zod';

/**
 * What a declared holiday must look like before it can be saved.
 *
 * Mirrors the backend's `holidays.validation.ts` — the server is the authority,
 * these rules only save a round trip. Keep the two in step.
 *
 * The one rule deliberately left to the server is whether the dates fall inside
 * the academic year: the session's own start and end are not in the browser,
 * and guessing them here would reject a valid date on an institute whose year runs
 * differently.
 */

export const HOLIDAY_TYPES = ['NATIONAL', 'FESTIVAL', 'REGIONAL', 'SCHOOL', 'VACATION'] as const;

/** Indian holiday names carry digits, dots, brackets and apostrophes. */
const NAME_PATTERN = /^[\p{L}\p{N} .,'()&/-]+$/u;

/** The `YYYY-MM-DD` a `<input type="date">` holds, and the API takes. */
const day = z
  .string()
  .trim()
  .min(1, 'Pick a date')
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use a date like 2026-08-15');

export const holidaySchema = z
  .object({
    name: z
      .string()
      .trim()
      .min(2, 'Give the holiday a name')
      .max(120, 'That name is too long')
      .regex(NAME_PATTERN, "Letters, numbers, spaces and . , ' ( ) & / - only"),
    startDate: day,
    endDate: day,
    type: z.enum(HOLIDAY_TYPES, {
      errorMap: () => ({ message: 'Choose what kind of holiday this is' }),
    }),
    description: z
      .string()
      .trim()
      .max(255, 'Keep the note under 255 characters')
      .transform((value) => value || undefined),
    isStaffWorking: z.boolean(),
  })
  // The commonest slip on this form, so the message lands on the field rather
  // than arriving as a server error after a round trip.
  .refine((value) => value.endDate >= value.startDate, {
    message: 'The last day cannot be before the first',
    path: ['endDate'],
  });

/** What the form holds while it is being filled in — every field a string. */
export interface HolidayFormValues {
  name: string;
  startDate: string;
  endDate: string;
  type: (typeof HOLIDAY_TYPES)[number];
  description: string;
  isStaffWorking: boolean;
  /**
   * Text the parents when declaring. Held by the form but not by
   * `holidaySchema`, which strips it — an edit must never send it.
   */
  notifyParentsBySms: boolean;
}

/** What the API takes, once parsed. */
export type HolidayPayload = z.infer<typeof holidaySchema>;

/**
 * The resolver the holiday form uses.
 *
 * `raw` keeps the form holding what the inputs hold. Without it the resolver
 * hands the submit handler the *transformed* values — an empty note already
 * `undefined` — and the `parse()` that builds the payload then rejects it as a
 * missing string, so declaring a holiday without a note would always fail.
 */
export const holidayFormResolver: Resolver<HolidayFormValues> = zodResolver(
  holidaySchema,
  undefined,
  { raw: true },
);

/** A new declaration starts as a one-day institute holiday — the commonest case. */
export const EMPTY_HOLIDAY_FORM: HolidayFormValues = {
  name: '',
  startDate: '',
  endDate: '',
  type: 'SCHOOL',
  description: '',
  isStaffWorking: false,
  notifyParentsBySms: false,
};
